import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/startups')({
  component: StartupsPage,
  head: () => ({
    meta: [
      { title: 'Uptime Monitoring for Startups | Uptrack' },
      { name: 'description', content: 'Uptime monitoring built for startups. Pro includes 50 monitors, status pages, and cron monitoring. Grow from Pro to Business without switching tools.' },
      { property: 'og:title', content: 'Uptime Monitoring for Startups | Uptrack' },
      { property: 'og:description', content: 'Ship your MVP, we watch it. 50 monitors on Pro, status pages, cron monitoring. Start free, or choose a plan for more.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-startups.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/startups' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Startups', item: 'https://uptrack.app/for/startups' },
          ],
        }),
      },
    ],
  }),
})

const pricingComparison = [
  { provider: 'UptimeRobot', free: '50 monitors / 5min', pro: '$7/mo for 50 / 1min', growth: 'Limited API' },
  { provider: 'Better Stack', free: '10 monitors / 3min', pro: '$34/mo per responder / 30s', growth: 'Per-seat pricing' },
  { provider: 'Pingdom', free: 'No free plan', pro: '$15/mo for 10', growth: 'Per-monitor pricing' },
  { provider: 'Uptrack', free: '5 monitors / 30s', pro: '$3/mo for 50 / 30s', growth: 'Flat pricing, all features', highlight: true },
]

function StartupsPage() {
  return (
    <FeaturePage
      eyebrow='For · Startups'
      section='Startups'
      title="Ship your MVP. We'll watch it."
      intro='Start with 50 monitors on Pro, with 30-second checks and status pages to build user trust. Cron monitoring is included, with larger plans available as you grow.'
      featuresHeading='Everything a startup needs on day one'
      features={[
        { title: 'Free to start', desc: 'Start free with 5 monitors, then choose Pro, Team, or Business based on your current monitoring needs.' },
        { title: '50 monitors on Pro', desc: 'Every Pro monitor runs at 30-second intervals. More than enough for many MVPs.' },
        { title: 'Status pages', desc: 'Give your users a public status page. Build trust before you have a track record.' },
        { title: 'Cron monitoring', desc: 'Make sure your background jobs actually run. Get alerted when a scheduled task misses its window.' },
        { title: 'Grow without switching', desc: 'Move from Pro ($3/mo) to Team ($20/mo) to Business. Same tool from MVP to Series B.' },
        { title: 'Fast setup', desc: 'Add your first monitor in under a minute. No agents to install, no config files to write.' },
      ]}
      table={{
        heading: 'How Uptrack scales with you',
        intro: 'Most tools get expensive fast. Uptrack stays predictable.',
        columns: ['Provider', 'Free Tier', 'Paid Tier', 'As You Grow'],
        rows: pricingComparison.map((c) => ({ cells: [c.provider, c.free, c.pro, c.growth], highlight: c.highlight })),
      }}
      why={{ heading: 'From launch day to product-market fit', body: 'You launch on Product Hunt. Traffic spikes. Uptrack detects a 502 within 30 seconds and emails you once several locations confirm it. You fix it before the HN crowd notices. Your public status page shows 99.9% uptime. Investors check it during due diligence.' }}
      cta={{ heading: 'Start free and upgrade when you need it', body: 'The free plan has 5 monitors at 30-second checks. Pro includes 50 monitors, status pages, and cron monitoring.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    />
  )
}
